/**
 * product-content-tabs
 *
 * Web Component for merchant-configured product tab blocks.
 * Manages aria-selected on tab buttons and the hidden attribute on tab panels.
 * Keyboard navigation follows ARIA authoring practices for tabs.
 */
class ProductContentTabs extends HTMLElement {
  connectedCallback() {
    this.tabList = this.querySelector('[role="tablist"]');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    if (!this.tabList || !this.tabs.length) return;

    this.boundOnTabClick = this.onTabClick.bind(this);
    this.boundOnKeyDown = this.onKeyDown.bind(this);

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.boundOnTabClick);
    });

    this.tabList.addEventListener('keydown', this.boundOnKeyDown);

    const preSelected = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true');
    if (!preSelected && this.tabs[0]) {
      this.activateTab(this.tabs[0], false);
    }
  }

  disconnectedCallback() {
    if (this.tabs) {
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.boundOnTabClick);
      });
    }
    if (this.tabList) {
      this.tabList.removeEventListener('keydown', this.boundOnKeyDown);
    }
  }

  onTabClick(event) {
    this.activateTab(event.currentTarget, false);
  }

  onKeyDown(event) {
    const { code } = event;
    const currentIndex = this.tabs.indexOf(document.activeElement);

    if (currentIndex === -1) return;

    if (code === 'ArrowRight') {
      event.preventDefault();
      const next = (currentIndex + 1) % this.tabs.length;
      this.activateTab(this.tabs[next]);
    } else if (code === 'ArrowLeft') {
      event.preventDefault();
      const prev = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      this.activateTab(this.tabs[prev]);
    } else if (code === 'Home') {
      event.preventDefault();
      this.activateTab(this.tabs[0]);
    } else if (code === 'End') {
      event.preventDefault();
      this.activateTab(this.tabs[this.tabs.length - 1]);
    }
  }

  activateTab(tab, setFocus = true) {
    this.tabs.forEach((t) => {
      t.setAttribute('aria-selected', 'false');
      t.setAttribute('tabindex', '-1');
    });
    this.panels.forEach((p) => {
      p.hidden = true;
    });

    tab.setAttribute('aria-selected', 'true');
    tab.setAttribute('tabindex', '0');

    const panelId = tab.getAttribute('aria-controls');
    const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
    if (panel) panel.hidden = false;

    tab.scrollIntoView({ inline: 'nearest', block: 'nearest' });

    if (setFocus) tab.focus();
  }
}

customElements.define('product-content-tabs', ProductContentTabs);
